// Angular
import { Inject, Injectable } from '@angular/core';

// Third party
import { Observable } from 'rxjs';
import * as moment from 'moment';

// Local library
import { CommonFacade, ENVIRONMENT } from '@amwager/data-access';

// Local file
import { FundingFacade } from '../+state';
import { IWithdrawalReview } from '../+state/funding.models';
import { FundingService } from './funding.service';

/**
 * Card brand abbreviations from the review payload, mapped to a display name and Material icon.
 */
const CARD_BRANDS: Record<string, { name: string; icon: string }> = {
  mc: { name: 'Mastercard', icon: 'credit_card' },
  vi: { name: 'Visa', icon: 'credit_card' },
  di: { name: 'Discover', icon: 'credit_card' }
};

/**
 * Review statuses mapped to their language key, English label and a colour class
 * defined in the stylesheet.
 * Kept in sync with the statuses the WithdrawalReviewStatus endpoint returns.
 */
const REVIEW_STATUSES: Record<string, { labelKey: string; label: string; color: string }> = {
  PendingReview: {
    labelKey: 'ui.funding.held.status.pending',
    label: 'Pending',
    color: 'status-warning'
  },
  Processing: {
    labelKey: 'ui.funding.held.status.processing',
    label: 'Processing',
    color: 'status-primary'
  },
  Completed: {
    labelKey: 'ui.funding.held.status.completed',
    label: 'Completed',
    color: 'status-info'
  },
  Denied: {
    labelKey: 'ui.funding.held.status.denied',
    label: 'Denied',
    color: 'status-danger'
  },
  Failed: {
    labelKey: 'ui.funding.held.status.failed',
    label: 'Failed',
    color: 'status-danger'
  },
  ManualIntervention: {
    labelKey: 'ui.funding.held.status.inreview',
    label: 'In Review',
    color: 'status-warning'
  },
  ApprovedAwaitingCustomer: {
    labelKey: 'ui.funding.held.status.approved',
    label: 'Approved',
    color: 'status-success'
  }
};

/**
 * Deliberately absent from the language packs: an unknown status has no copy to
 * resolve, so the pipe falls through to the raw status passed as the fallback.
 */
const UNKNOWN_STATUS_KEY = 'ui.funding.held.status.unknown';

/** Business logic for the held withdrawals component. */
@Injectable()
export class HeldService {

  constructor(
    private readonly fundingFacade: FundingFacade,
    private readonly commonFacade: CommonFacade,
    private readonly fundingService: FundingService,
    @Inject(ENVIRONMENT) private readonly environment: string,
  ) {}

  /** Dispatch the held withdrawals request and stream the result. */
  loadReviews$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccess: boolean | undefined
  }> {
    this.fundingFacade.getHeldWithdrawals();
    return this.fundingFacade.isHeldWithdrawalsSuccess$();
  }

  /** Brand followed by the masked card number, e.g. "Visa •••• 4242". */
  cardLabel(review: IWithdrawalReview): string {
    if (review.PaymentMethod && review.PaymentMethod.toLowerCase() !== 'card') {
      return review.InstrumentName || review.PaymentMethod;
    }

    const brand = this.findBrand(review.CardType);
    const name = brand ? brand.name : (review.CardType || 'Card');

    return review.LastFour ? `${name} •••• ${review.LastFour}` : name;
  }

  brandIcon(review: IWithdrawalReview): string {
    const brand = this.findBrand(review.CardType);
    return brand ? brand.icon : 'credit_card';
  }

  statusLabel(review: IWithdrawalReview): string {
    return REVIEW_STATUSES[review.Status]?.label || review.Status;
  }

  /** Language key for the status badge, paired with `statusLabel` as the fallback. */
  statusKey(review: IWithdrawalReview): string {
    return REVIEW_STATUSES[review.Status]?.labelKey || UNKNOWN_STATUS_KEY;
  }

  statusColor(review: IWithdrawalReview): string {
    return REVIEW_STATUSES[review.Status]?.color || 'status-secondary';
  }

  /** Renders the UTC timestamp in the customer's local time. */
  requestedTime(review: IWithdrawalReview): string {
    return moment.utc(review.RequestedAtUtc).local().format('MMM DD, YYYY h:mm A');
  }

  /** Flag the app as offline for the given source. */
  setOffline(source: string): void {
    this.commonFacade.setIsConnectedInternet(source, false);
  }

  /** Watch connectivity and report changes back to the caller. */
  checkConnectivity(source: string, onChange: (online: boolean) => void): void {
    this.fundingService.checkConnectivity(this.environment, source, onChange);
  }

  private findBrand(cardType?: string): { name: string; icon: string } | undefined {
    return CARD_BRANDS[cardType ? cardType.toLowerCase() : ''];
  }

}
